import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { EVIDENCE } from "@/lib/constants";
import { Combobox } from "@/components/Combobox";
import { Field, GhostButton, labelCls, PrimaryButton } from "@/components/FormBits";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const NONE = [];
const uniq = (arr) => [...new Set(arr)].sort((a, b) => a.localeCompare(b));

export function EvidenceTag({ status, userAdded, testId }) {
  const label = userAdded ? "Not yet verified · added by you" : EVIDENCE[status]?.label || EVIDENCE.unverified.label;
  const tone = userAdded || status === "unverified" || status === "name_only" ? "bg-[#F6F1EA] text-[#6E5549] border-[#E6DBCF]"
    : status === "partial" ? "bg-[#FAF2E6] text-[#9C6218] border-[#efdcbc]" : "bg-[#EAF3ED] text-[#2E6B4F] border-[#cfe3d6]";
  return <span data-testid={testId} className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${tone}`}>{label}</span>;
}

function MissingPigment({ onAdd, busy }) {
  const [f, setF] = useState({ brand: "", line: "", shade: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.brand.trim() || !f.shade.trim()) return toast.error("Please enter at least the brand and exact shade.");
    if (await onAdd(f)) setF({ brand: "", line: "", shade: "" });
  };
  return (
    <Collapsible className="rounded-2xl border border-dashed border-[#D99B90] bg-[#FDF2F0]">
      <CollapsibleTrigger data-testid="kit-missing-toggle" className="w-full px-5 py-4 text-left text-sm font-semibold text-[#2C1810] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C5A059]">
        Can't find your pigment?
      </CollapsibleTrigger>
      <CollapsibleContent>
        <form onSubmit={submit} className="space-y-4 px-5 pb-5">
          <p className="text-sm leading-relaxed text-[#4A3B32]">You can save this pigment now. Its ingredients will need verification before ingredient-based guidance is available.</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Brand *" id="kit-missing-brand" value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} />
            <Field label="Line" id="kit-missing-line" value={f.line} onChange={(e) => setF({ ...f, line: e.target.value })} />
            <Field label="Shade *" id="kit-missing-shade" value={f.shade} onChange={(e) => setF({ ...f, shade: e.target.value })} />
          </div>
          <GhostButton type="submit" disabled={busy} data-testid="kit-missing-submit" className="w-full bg-white sm:w-auto">Save as unverified pigment</GhostButton>
        </form>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function KitBuilder() {
  const qc = useQueryClient();
  const pigQ = useQuery({ queryKey: ["pigments"], queryFn: async () => (await api.get("/pigments")).data });
  const kitQ = useQuery({ queryKey: ["kit"], queryFn: async () => (await api.get("/kit")).data });
  const pigments = pigQ.data ?? NONE;
  const kit = kitQ.data ?? NONE;
  const [sel, setSel] = useState({ brand: "", line: "", id: "" });
  const [busy, setBusy] = useState(false);

  const brands = useMemo(() => uniq(pigments.map((p) => p.brand)), [pigments]);
  const lines = useMemo(() => uniq(pigments.filter((p) => p.brand === sel.brand).map((p) => p.line)), [pigments, sel.brand]);
  const shades = useMemo(() => pigments.filter((p) => p.brand === sel.brand && p.line === sel.line && p.shade), [pigments, sel]);
  const chosen = pigments.find((p) => p.id === sel.id);
  const inKit = chosen && kit.some((k) => k.pigment_id === chosen.id);

  const add = async (payload) => {
    setBusy(true);
    try {
      await api.post("/kit", payload);
      await qc.invalidateQueries({ queryKey: ["kit"] });
      toast.success("Saved to your kit");
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/kit/${id}`);
      await qc.invalidateQueries({ queryKey: ["kit"] });
      toast.success("Removed from your kit");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (pigQ.isError || kitQ.isError) {
    return (
      <div className="rounded-2xl bg-[#F9ECEC] p-5 text-sm text-[#8C2A2A]" role="alert" data-testid="kit-load-error">
        We couldn't load the pigment library. Check your connection and{" "}
        <button className="font-semibold underline" onClick={() => { pigQ.refetch(); kitQ.refetch(); }}>try again</button>.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-2xl border border-[#E6DBCF] bg-white/60 p-5">
        <div className="grid gap-4 sm:grid-cols-3">
          <div><span className={labelCls}>Brand</span><div className="mt-1.5">
            <Combobox testId="kit-brand-select" disabled={pigQ.isLoading} options={brands.map((b) => ({ value: b, label: b }))} value={sel.brand}
              onChange={(v) => setSel({ brand: v, line: "", id: "" })} placeholder={pigQ.isLoading ? "Loading…" : "Choose brand"} searchPlaceholder="Search brands…" />
          </div></div>
          <div><span className={labelCls}>Product line</span><div className="mt-1.5">
            <Combobox testId="kit-line-select" disabled={!sel.brand} options={lines.map((l) => ({ value: l, label: l }))} value={sel.line}
              onChange={(v) => setSel({ ...sel, line: v, id: "" })} placeholder="Choose line" searchPlaceholder="Search lines…" />
          </div></div>
          <div><span className={labelCls}>Exact shade</span><div className="mt-1.5">
            <Combobox testId="kit-shade-select" disabled={!sel.line || !shades.length} options={shades.map((p) => ({ value: p.id, label: p.shade, sub: kit.some((k) => k.pigment_id === p.id) ? "In kit" : "" }))}
              value={sel.id} onChange={(v) => setSel({ ...sel, id: v })} placeholder={sel.line && !shades.length ? "No shades recorded" : "Choose shade"} searchPlaceholder="Search shades…" />
          </div></div>
        </div>
        {sel.line && !shades.length && (
          <p className="text-xs text-[#6E5549]" data-testid="kit-line-no-shades">No individual shades are recorded for this line yet. Use “Can't find your pigment?” below to save yours as unverified.</p>
        )}
        {chosen && (
          <div className="rounded-xl bg-[#FDFBF7] p-4" data-testid="kit-selected-preview">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-serif text-xl font-semibold text-[#2C1810]">{chosen.brand} · {chosen.shade}</p>
              <EvidenceTag status={chosen.verification_status} testId="kit-selected-evidence" />
            </div>
            <p className="mt-1 text-xs leading-relaxed text-[#6E5549]">{EVIDENCE[chosen.verification_status]?.hint}</p>
            <p className="mt-1 text-xs text-[#6E5549]"><span className="font-semibold">Source note:</span> {chosen.source_note}</p>
            {chosen.source_url && <a href={chosen.source_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-[#9C6218] underline">View source <ExternalLink size={11} /></a>}
          </div>
        )}
        <PrimaryButton type="button" data-testid="kit-add-selected-btn" disabled={!chosen || inKit || busy} onClick={async () => (await add({ pigment_id: chosen.id })) && setSel({ ...sel, id: "" })} className="w-full sm:w-auto">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} {inKit ? "Already in your kit" : "Add to my kit"}
        </PrimaryButton>
      </div>

      <MissingPigment onAdd={add} busy={busy} />

      <div>
        <p className={labelCls}>Your kit · <span data-testid="kit-count">{kit.length}</span> saved</p>
        <ul className="mt-3 space-y-2" data-testid="kit-saved-list">
          {kitQ.isLoading && <li className="text-sm text-[#A88B7D]">Loading your kit…</li>}
          {!kitQ.isLoading && !kit.length && <li className="rounded-xl bg-[#F6F1EA] px-4 py-3 text-sm italic text-[#6E5549]" data-testid="kit-empty-state">No pigments saved yet.</li>}
          {kit.map((k) => (
            <li key={k.id} data-testid={`kit-saved-${k.id}`} className="flex items-center gap-3 rounded-xl border border-[#E6DBCF] bg-white/70 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-[#2C1810]">{k.brand} · {k.shade || k.line}</p>
                <p className="truncate text-xs text-[#A88B7D]">{k.line}</p>
              </div>
              <EvidenceTag status={k.pigment?.verification_status} userAdded={!k.pigment_id} testId={`kit-saved-evidence-${k.id}`} />
              <button type="button" onClick={() => remove(k.id)} data-testid={`kit-remove-${k.id}`} aria-label={`Remove ${k.brand} ${k.shade}`}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[#A88B7D] hover:bg-[#F9ECEC] hover:text-[#8C2A2A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C5A059]">
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
